import { keyBy as _keyBy } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import Button from '@components/uikit/Button/Button';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';

import { filterLocationTreeBySearchValue } from '@helpers/filterLocationTreeBySearchValue';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import { getFacilityLevelActiveWithMultiplicationFactorValue } from '../../helpers/getFacilityLevelActiveWithMultiplicationFactorValue';
import { useEquipmentSettingsListQuery } from '../../services/useEquipmentSettingsListQuery';
import CostSavingCalculationItem from '../CostSavingCalculationItem/CostSavingCalculationItem';
import TableCostSavingCalculationSettingsSkeleton from './TableCostSavingCalculationSettingsSkeleton';
import style from './style.module.scss';

const CostSavingCalculationSettings = () => {
  const { t } = useTranslation();

  const { data: equipmentSettingsListData, isLoading: equipmentSettingsListIsLoading } =
    useEquipmentSettingsListQuery();

  const { customerSettings, facilitySettings, locationSettings } = equipmentSettingsListData || {};

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [searchValue, setSearchValue] = useState('');

  const { data: assetTreeData } = useAssetTreeQuery();

  const { facilityLevelActiveWithMultiplicationFactorValue, allFacilitiesIds, totalFacilitiesCount } =
    useMemo(() => {
      if (customerSettings || facilitySettings || locationSettings) {
        const facilitySettingsByKey = _keyBy(facilitySettings, 'facilityId');
        const locationSettingsByKey = _keyBy(locationSettings, 'assetId');

        const { facilityLevelActiveWithMultiplicationFactorValue, allFacilitiesIds, totalFacilitiesCount } =
          getFacilityLevelActiveWithMultiplicationFactorValue({
            facilityLevelActive: assetTreeData?.facilities || [],
            facilitySettingsByKey,
            locationSettingsByKey,
            defaultMultiplicationFactor: customerSettings?.multiplicationFactor ?? 0,
          });

        return { facilityLevelActiveWithMultiplicationFactorValue, allFacilitiesIds, totalFacilitiesCount };
      }

      return {
        facilityLevelActiveWithMultiplicationFactorValue: [],
        allFacilitiesIds: {},
        totalFacilitiesCount: 0,
      };
    }, [assetTreeData?.facilities, customerSettings, facilitySettings, locationSettings]);

  const isExpandedAll = Object.values(expandedRows).filter((value) => value).length === totalFacilitiesCount;

  const handleChange = (value: string) => {
    if (value && !isExpandedAll) {
      setExpandedRows(allFacilitiesIds);
    }

    if (!value && isExpandedAll) {
      setExpandedRows({});
    }

    setSearchValue(value);
  };

  const filteredFacilityLevelActiveWithMultiplicationFactorValue = filterLocationTreeBySearchValue(
    facilityLevelActiveWithMultiplicationFactorValue,
    searchValue,
  );

  return (
    <div className={style.costSavingSettingsWrapper}>
      <div className={style.costSavingSettingsHeader}>
        <h4 className={style.costSavingSettingsTitle}>{t('Cost Saving Calculation Settings')}</h4>
        <div className={style.searchPanel}>
          <FilterSearch
            value={searchValue}
            onChange={handleChange}
            placeholder={t('Start to type...')}
            variant="table-search"
          />
        </div>
      </div>
      <table className={style.costSavingSettingsTable}>
        <thead>
          <tr>
            <th className={style.facilityColumn}>{`${t('Facility')}/${t('Location')}`}</th>
            <th>{t('Multiplication Factor')}</th>
            {!equipmentSettingsListIsLoading &&
              filteredFacilityLevelActiveWithMultiplicationFactorValue?.length && (
                <th className={style.actionsColumn}>
                  <Button
                    type="secondary"
                    text={isExpandedAll ? t('Collapse all') : t('Expand all')}
                    onClick={() => {
                      isExpandedAll ? setExpandedRows({}) : setExpandedRows(allFacilitiesIds);
                    }}
                  />
                </th>
              )}
          </tr>
        </thead>
        <tbody>
          {equipmentSettingsListIsLoading ? (
            <TableCostSavingCalculationSettingsSkeleton />
          ) : filteredFacilityLevelActiveWithMultiplicationFactorValue?.length ? (
            filteredFacilityLevelActiveWithMultiplicationFactorValue.map((row) => (
              <CostSavingCalculationItem
                key={row.id}
                row={row}
                expandedRows={expandedRows}
                setExpandedRows={setExpandedRows}
              />
            ))
          ) : (
            <TableEmptyRowExtended text={t('No Locations Were Found.')} className={style.emptyRow} />
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CostSavingCalculationSettings;
